import { useCallback, useEffect, useRef, useState } from 'react';
import {
  DEFAULT_CONTENT_LOCALE,
  type LessonMetadataTranslationPatch,
  type LessonMetadataTranslationProjection,
  type TranslationReplacementAction,
} from '@course-authoring/shared-types';
import { useLessonMetadataMutation, useLessonMetadataQuery } from '../../api/lessonMetadataApi';
import { LessonMetadataPane } from '../../components/LessonMetadataPane';
import type { AutosaveStatus } from '../../hooks/useAutosave';
import type { LessonMeta } from '../../store/editorStore';
import { lessonEditorPageStyles as styles } from './lessonEditorPageStyles';
import { useLessonMetadataAutosave } from './useLessonMetadataAutosave';

export interface LessonMetadataSaveState {
  status: AutosaveStatus;
  isPending: boolean;
}

interface LessonEditorMetadataProps {
  lessonId: string;
  lessonMeta: LessonMeta;
  selectedLocale: string;
  selectedLanguageLabel: string;
  metadataAccountId: number | null;
  onRegisterFlush: (flush: (() => Promise<void>) | null) => void;
  onSaveStateChange: (state: LessonMetadataSaveState) => void;
}

export function LessonEditorMetadata({
  lessonId,
  lessonMeta,
  selectedLocale,
  selectedLanguageLabel,
  metadataAccountId,
  onRegisterFlush,
  onSaveStateChange,
}: LessonEditorMetadataProps) {
  const metadataScopeKey = `${lessonId}:${selectedLocale}:${metadataAccountId ?? 'base'}`;
  const {
    data: projection,
    isLoading,
    error,
  } = useLessonMetadataQuery(lessonId, {
    enabled: Boolean(lessonId),
    locale: selectedLocale,
    accountId: metadataAccountId,
  });
  const [stagedReplacementAction, setStagedReplacementAction] = useState<TranslationReplacementAction>();
  const [sessionReplacementView, setSessionReplacementView] = useState<'prompt' | 'preserved' | null>(null);
  const revisingReplacementRef = useRef(false);
  const awaitingReplacementDecisionRef = useRef(false);

  const mutation = useLessonMetadataMutation(lessonId, {
    locale: selectedLocale,
    accountId: metadataAccountId,
    onError: (metadataSaveError) => {
      console.error('Failed to save lesson metadata:', metadataSaveError);
    },
  });

  const handleSaved = useCallback((
    _projection: LessonMetadataTranslationProjection,
    patch: LessonMetadataTranslationPatch,
  ) => {
    const sourceChanged = 'title' in patch || 'description' in patch;
    if (patch.replacementAction) {
      // The queued note derives from the refreshed projection's pending count.
      setSessionReplacementView(patch.replacementAction === 'preserve' ? 'preserved' : null);
      setStagedReplacementAction(undefined);
      revisingReplacementRef.current = false;
      awaitingReplacementDecisionRef.current = false;
    } else if (sourceChanged) {
      awaitingReplacementDecisionRef.current = true;
    }
  }, []);

  const autosave = useLessonMetadataAutosave({
    enabled: Boolean(projection) && projection?.editable !== false,
    scopeKey: metadataScopeKey,
    initialProjection: projection,
    includeEstimatedTime: selectedLocale === DEFAULT_CONTENT_LOCALE,
    replacementAction: stagedReplacementAction,
    save: mutation.mutateAsync,
    onSaved: handleSaved,
  });

  useEffect(() => {
    setStagedReplacementAction(undefined);
    setSessionReplacementView(null);
    revisingReplacementRef.current = false;
    awaitingReplacementDecisionRef.current = false;
  }, [metadataScopeKey]);

  useEffect(() => {
    onRegisterFlush(autosave.flushIfDirty);
    return () => onRegisterFlush(null);
  }, [autosave.flushIfDirty, onRegisterFlush]);

  useEffect(() => {
    onSaveStateChange({ status: autosave.status, isPending: mutation.isPending });
  }, [autosave.status, mutation.isPending, onSaveStateChange]);

  const persistedQueued = (projection?.summary.pendingHumanTranslationCount ?? 0) > 0;
  const replacementView: 'prompt' | 'preserved' | 'queued' | null = sessionReplacementView === 'prompt'
    ? 'prompt'
    : sessionReplacementView === 'preserved'
      ? 'preserved'
      : persistedQueued
        ? 'queued'
        : null;

  const handleTranslatableFieldFocus = useCallback(() => {
    if (
      selectedLocale === DEFAULT_CONTENT_LOCALE
      && (projection?.summary.humanTranslationCount ?? 0) > 0
      && replacementView === null
    ) {
      setSessionReplacementView('prompt');
    }
  }, [projection?.summary.humanTranslationCount, replacementView, selectedLocale]);

  const handleReplacementDecision = useCallback(async (action: TranslationReplacementAction) => {
    // The latest answer supersedes any earlier one before anything can save:
    // synchronously in the autosave scope (an in-flight or failed save must
    // never resurrect a superseded answer) and in React state. On a failed
    // commit the answer stays staged and rides the next successful save.
    autosave.stageReplacementAction(action);
    setStagedReplacementAction(action);
    const sourceChangeExists = revisingReplacementRef.current
      || awaitingReplacementDecisionRef.current
      || autosave.hasPendingSourceChanges();
    if (!sourceChangeExists) {
      // No English change yet (prompt opened on focus): hold the answer
      // uncommitted so no translation row is written until a source change
      // first saves.
      return;
    }
    try {
      await autosave.saveDecision(action);
    } catch {
      // The autosave hook owns the error status and the mutation logs the API error.
    }
  }, [autosave.hasPendingSourceChanges, autosave.saveDecision, autosave.stageReplacementAction]);

  const handleChangeReplacementDecision = useCallback(() => {
    revisingReplacementRef.current = true;
    setSessionReplacementView('prompt');
  }, []);

  const isLocked = projection?.editable === false;
  const metadataPaneMeta = {
    ...lessonMeta,
    title: autosave.draft.title,
    description: autosave.draft.description,
    estimatedTimeMinutes: autosave.draft.estimatedTimeMinutes,
  };

  return (
    <div style={styles.mainLayout}>
      {error && !projection ? (
        <div style={styles.error}>Error loading lesson metadata: {(error as Error).message}</div>
      ) : isLoading || !projection ? (
        <div style={styles.loading}>Loading lesson metadata...</div>
      ) : (
        <LessonMetadataPane
          lessonMeta={metadataPaneMeta}
          mode={selectedLocale === DEFAULT_CONTENT_LOCALE ? 'english' : 'translation'}
          locked={isLocked}
          language={selectedLanguageLabel}
          translationStatus={projection.status ?? undefined}
          translationReadOnly={!projection.editable}
          humanTranslationCount={projection.summary.humanTranslationCount}
          replacementView={replacementView}
          isSubmitting={mutation.isPending}
          error={error ? (error as { message?: string }).message ?? 'Failed to load metadata' : null}
          onTitleChange={(title) => autosave.setDraft((current) => ({ ...current, title }))}
          onDescriptionChange={(description) => autosave.setDraft((current) => ({ ...current, description }))}
          onEstimatedTimeMinutesChange={(estimatedTimeMinutes) =>
            autosave.setDraft((current) => ({ ...current, estimatedTimeMinutes }))}
          onTranslatableFieldFocus={handleTranslatableFieldFocus}
          onQueueTranslations={() => { void handleReplacementDecision('queue'); }}
          onPreserveTranslations={() => { void handleReplacementDecision('preserve'); }}
          onChangeReplacementDecision={handleChangeReplacementDecision}
        />
      )}
    </div>
  );
}
